import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../../contexts/HospitalOnboardingContext';
import {
  Plus,
  Trash2,
  Mic,
  ChevronDown,
  X,
  Bookmark,
  ArrowRight
} from 'lucide-react';
import { getAmbulanceTypeAbbreviation } from '../../../utils/ambulanceTypeHelper';

export const DISTANCE_SLABS = [
  { key: 'slab_0_10', label: '0–10 km (₹)' },
  { key: 'slab_10_15', label: '10.1–15 km (₹)' },
  { key: 'slab_15_20', label: '15.1–20 km (₹)' },
  { key: 'slab_20_25', label: '20.1–25 km (₹)' },
  { key: 'slab_25_30', label: '25.1–30 km (₹)' },
  { key: 'slab_30_35', label: '30.1–35 km (₹)' },
  { key: 'slab_35_40', label: '35.1–40 km (₹)' },
  { key: 'slab_40_45', label: '40.1–45 km (₹)' },
  { key: 'slab_45_50', label: '45.1–50 km (₹)' },
  { key: 'slab_50_55', label: '50.1–55 km (₹)' },
  { key: 'slab_55_60', label: '55.1–60 km (₹)' },
  { key: 'slab_60_65', label: '60.1–65 km (₹)' },
  { key: 'slab_65_70', label: '65.1–70 km (₹)' },
  { key: 'slab_70_75', label: '70.1–75 km (₹)' },
  { key: 'slab_75_80', label: '75.1–80 km (₹)' },
  { key: 'slab_80_85', label: '80.1–85 km (₹)' },
  { key: 'slab_85_90', label: '85.1–90 km (₹)' },
  { key: 'slab_90_95', label: '90.1–95 km (₹)' },
  { key: 'slab_95_100', label: '95.1–100 km (₹)' }
];

const DEFAULT_AMBULANCE_TYPES = [
  'ALS',
  'BLS Big',
  'BLS Small',
  'ACLS',
  'NICU/PICU',
  'Mortuary',
  'PTV',
  'Other'
];

const VEHICLE_TYPES = [
  'Force Traveller',
  'Tata Winger',
  'Mahindra Bolero',
  'Maruti Omni',
  'Maruti Eeco',
  'Toyota Innova',
  'Other Vehicle'
];

const ADDON_TYPES = [
  'Doctor',
  'Paramedic / Nurse',
  'Ventilator Support',
  'Syringe Pump',
  'Oxygen Cylinder (Extra)',
  'Cardiac Monitor',
  'Spine Board / Scoop Stretcher',
  'Suction Machine',
  'Other Add-on'
];

const CHARGED_PER_OPTIONS = [
  'Per Trip',
  'Per Hour',
  'Per 12 Hours',
  'Per 24 Hours',
  'Per Day',
  'Flat Rate',
  'At Actuals'
];

const POPULAR_CITIES = [
  'Ahmedabad',
  'Surat',
  'Vadodara',
  'Rajkot',
  'Gandhinagar',
  'Bhavnagar',
  'Jamnagar',
  'Mumbai',
  'Pune',
  'Nagpur',
  'Delhi NCR',
  'Noida',
  'Gurugram',
  'Bengaluru',
  'Hyderabad',
  'Chennai',
  'Kolkata',
  'Jaipur',
  'Lucknow',
  'Chandigarh',
  'Indore',
  'Bhopal'
];

const createInitialCity = (index, defaultCity = '') => ({
  id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `city-${window.crypto.randomUUID()}` : `city-${Math.random().toString(36).slice(2, 9)}`,
  selectedCity: defaultCity || '',
  ambulanceFares: [
    {
      id: 'af-1',
      ambulance_type: '',
      vehicle_type: '',
      equipment: '',
      slabs: {},
      outstation_rate: ''
    }
  ],
  addonsByDistance: [
    {
      id: 'aod-1',
      addon: '',
      details: '',
      slabs: {},
      outstation_charge: ''
    }
  ],
  otherCharges: [
    {
      id: 'oc-1',
      service_item: '',
      amount: '',
      charged_per: '',
      notes: ''
    }
  ],
  waitingCharges: {
    free_waiting_minutes: '',
    after_free_period_rate: '',
    waiting_terms: ''
  },
  rateCardNotes: {
    notes: ''
  }
});

export const Step4Rates = () => {
  const navigate = useNavigate();
  const {
    formData,
    updateFields,
    saveAndContinue,
    goBack,
    saveAndExit,
    isSaving,
    ambulanceTypeNames
  } = useHospitalOnboarding();

  // Combined ambulance types lookup from context or defaults
  const availableAmbulanceTypes = Array.from(
    new Set([...(ambulanceTypeNames || []), ...DEFAULT_AMBULANCE_TYPES])
  );

  // City rate cards state
  const [cities, setCities] = useState(() => {
    if (Array.isArray(formData.city_rate_cards) && formData.city_rate_cards.length > 0) {
      return formData.city_rate_cards;
    }
    const defaultCity = formData.city || formData.service_area || '';
    return [createInitialCity(0, defaultCity)];
  });

  const [activeCityIndex, setActiveCityIndex] = useState(0);

  const currentCity = cities[activeCityIndex] || cities[0];

  const updateCurrentCity = (updater) => {
    setCities((prev) => {
      const next = [...prev];
      const target = next[activeCityIndex] || next[0];
      next[activeCityIndex] = typeof updater === 'function' ? updater(target) : { ...target, ...updater };
      return next;
    });
  };

  // City management
  const handleAddCity = () => {
    const newCity = createInitialCity(cities.length);
    setCities((prev) => [...prev, newCity]);
    setActiveCityIndex(cities.length);
  };

  const handleRemoveCity = (indexToRemove) => {
    if (cities.length <= 1) return;
    setCities((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    if (activeCityIndex >= indexToRemove && activeCityIndex > 0) {
      setActiveCityIndex(activeCityIndex - 1);
    }
  };

  const handleCityNameChange = (val) => {
    updateCurrentCity((curr) => ({
      ...curr,
      selectedCity: val
    }));
  };

  // 1. Ambulance Fares row management
  const handleAddAmbulanceFareRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `af-${window.crypto.randomUUID()}` : `af-${Math.random().toString(36).slice(2, 9)}`,
      ambulance_type: '',
      vehicle_type: '',
      equipment: '',
      slabs: {},
      outstation_rate: ''
    };
    updateCurrentCity((curr) => ({
      ...curr,
      ambulanceFares: [...curr.ambulanceFares, newRow]
    }));
  };

  const handleRemoveAmbulanceFareRow = (id) => {
    updateCurrentCity((curr) => {
      if (curr.ambulanceFares.length <= 1) return curr;
      return {
        ...curr,
        ambulanceFares: curr.ambulanceFares.filter((r) => r.id !== id)
      };
    });
  };

  const handleAmbulanceFareChange = (id, field, value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      ambulanceFares: curr.ambulanceFares.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    }));
  };

  const handleAmbulanceFareSlabChange = (id, slabKey, value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      ambulanceFares: curr.ambulanceFares.map((r) => {
        if (r.id !== id) return r;
        return {
          ...r,
          slabs: {
            ...r.slabs,
            [slabKey]: value
          }
        };
      })
    }));
  };

  // 2. Add-ons by Distance row management
  const handleAddAddonRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `aod-${window.crypto.randomUUID()}` : `aod-${Math.random().toString(36).slice(2, 9)}`,
      addon: '',
      details: '',
      slabs: {},
      outstation_charge: ''
    };
    updateCurrentCity((curr) => ({
      ...curr,
      addonsByDistance: [...curr.addonsByDistance, newRow]
    }));
  };

  const handleRemoveAddonRow = (id) => {
    updateCurrentCity((curr) => {
      if (curr.addonsByDistance.length <= 1) return curr;
      return {
        ...curr,
        addonsByDistance: curr.addonsByDistance.filter((r) => r.id !== id)
      };
    });
  };

  const handleAddonChange = (id, field, value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      addonsByDistance: curr.addonsByDistance.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    }));
  };

  const handleAddonSlabChange = (id, slabKey, value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      addonsByDistance: curr.addonsByDistance.map((r) => {
        if (r.id !== id) return r;
        return {
          ...r,
          slabs: {
            ...r.slabs,
            [slabKey]: value
          }
        };
      })
    }));
  };

  // 3. Other Charges row management
  const handleAddOtherChargeRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `oc-${window.crypto.randomUUID()}` : `oc-${Math.random().toString(36).slice(2, 9)}`,
      service_item: '',
      amount: '',
      charged_per: '',
      notes: ''
    };
    updateCurrentCity((curr) => ({
      ...curr,
      otherCharges: [...curr.otherCharges, newRow]
    }));
  };

  const handleRemoveOtherChargeRow = (id) => {
    updateCurrentCity((curr) => {
      if (curr.otherCharges.length <= 1) return curr;
      return {
        ...curr,
        otherCharges: curr.otherCharges.filter((r) => r.id !== id)
      };
    });
  };

  const handleOtherChargeChange = (id, field, value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      otherCharges: curr.otherCharges.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    }));
  };

  // 4. Waiting charges
  const handleWaitingChargesChange = (field, value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      waitingCharges: {
        ...curr.waitingCharges,
        [field]: value
      }
    }));
  };

  // 5. Rate card notes
  const handleRateCardNotesChange = (value) => {
    updateCurrentCity((curr) => ({
      ...curr,
      rateCardNotes: {
        notes: value
      }
    }));
  };

  const syncStateToFormData = () => {
    // Generate backwards-compatible rate_cards array for Step 6 summary
    const flatRateCards = [];
    cities.forEach((c) => {
      (c.ambulanceFares || []).forEach((af) => {
        if (af.ambulance_type) {
          flatRateCards.push({
            id: af.id,
            city: c.selectedCity || 'Primary',
            ambulance_type: af.ambulance_type,
            vehicle_type: af.vehicle_type,
            base_rate: af.slabs?.slab_0_10 || '0',
            per_km: af.outstation_rate || '0',
            others: af.equipment || ''
          });
        }
      });
    });

    updateFields({
      city_rate_cards: cities,
      rate_cards: flatRateCards.length > 0 ? flatRateCards : formData.rate_cards || []
    });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    syncStateToFormData();
    saveAndContinue();
  };

  const handleSaveAndExit = () => {
    syncStateToFormData();
    saveAndExit();
  };

  return (
    <div className="w-full">
      {/* Header Row: Title & Switch to Voice with Blue Brand Theme */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 4 OF 6
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Rate card
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Enter city-wise fares by ambulance, vehicle and distance slab. All amounts are in rupees (₹).
          </p>
          {/* Smart Ambulance Blue Accent Line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=fleet&step=4')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium shadow-2xs transition-colors self-start cursor-pointer"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* CARD 1: City rate cards */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-start justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-slate-800">City rate cards</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Create a separate rate card for each city you serve.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddCity}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-medium text-blue-600 hover:bg-blue-50/50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add City</span>
            </button>
          </div>

          {/* City selection buttons / tabs with Blue Active Style */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            {cities.map((city, idx) => {
              const isActive = activeCityIndex === idx;
              return (
                <div key={city.id} className="relative group">
                  <button
                    type="button"
                    onClick={() => setActiveCityIndex(idx)}
                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-xs font-semibold'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {city.selectedCity ? city.selectedCity : `City ${idx + 1}`}
                  </button>
                  {cities.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveCity(idx);
                      }}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-300 hover:bg-red-500 text-white flex items-center justify-center text-[10px] shadow-xs cursor-pointer opacity-80 hover:opacity-100 transition-opacity"
                      title="Remove this city rate card"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* City Dropdown */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              City
            </label>
            <div className="relative max-w-md">
              <select
                value={currentCity.selectedCity}
                onChange={(e) => handleCityNameChange(e.target.value)}
                className="w-full px-3.5 py-2.5 pr-8 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all appearance-none cursor-pointer"
              >
                <option value="">Select city</option>
                {POPULAR_CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-1.5 text-xs text-slate-500 pt-2 border-t border-slate-100">
            <p>
              Enter a fixed fare for each distance slab and a per-km outstation rate. Add a row for each ambulance and vehicle combination. Scroll the table sideways to see all slabs.
            </p>
            <p>
              Use Other charges for items such as a Home Freezer Box, with separate rows for 12-hour and 24-hour charges.
            </p>
          </div>
        </div>

        {/* CARD 2: Ambulance fares */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-slate-800">Ambulance fares</h3>
              <p className="text-xs text-slate-400">Configured tariffs by distance tier</p>
            </div>
            <button
              type="button"
              onClick={handleAddAmbulanceFareRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-medium text-blue-600 hover:bg-blue-50/50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto pb-3">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 font-semibold whitespace-nowrap">
                  <th className="py-2.5 px-3 w-10 text-center">#</th>
                  <th className="py-2.5 px-3 min-w-[170px]">Ambulance Type</th>
                  <th className="py-2.5 px-3 min-w-[170px]">Vehicle Type</th>
                  <th className="py-2.5 px-3 min-w-[220px]">Equipment / Staffing / Other Vehicle</th>
                  {DISTANCE_SLABS.map((slab) => (
                    <th key={slab.key} className="py-2.5 px-2.5 min-w-[95px] text-center font-semibold text-slate-600">
                      {slab.label}
                    </th>
                  ))}
                  <th className="py-2.5 px-3 min-w-[130px] text-center font-semibold text-slate-600">Outstation (₹/km)</th>
                  <th className="py-2.5 px-2 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentCity.ambulanceFares.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-semibold text-slate-500 text-center">{idx + 1}</td>
                    {/* Ambulance Type */}
                    <td className="py-3 px-3">
                      <div className="relative">
                        <select
                          value={row.ambulance_type}
                          onChange={(e) => handleAmbulanceFareChange(row.id, 'ambulance_type', e.target.value)}
                          title={row.ambulance_type || 'Select an option'}
                          className="w-full py-2 pl-3 pr-7 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select an option</option>
                          {availableAmbulanceTypes.map((t) => {
                            const abbr = getAmbulanceTypeAbbreviation(t) || t;
                            return (
                              <option key={t} value={abbr} title={t}>{abbr}</option>
                            );
                          })}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    {/* Vehicle Type */}
                    <td className="py-3 px-3">
                      <div className="relative">
                        <select
                          value={row.vehicle_type}
                          onChange={(e) => handleAmbulanceFareChange(row.id, 'vehicle_type', e.target.value)}
                          className="w-full py-2 pl-3 pr-7 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select an option</option>
                          {VEHICLE_TYPES.map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    {/* Equipment / Staffing / Other Vehicle */}
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="e.g. Ventilator, Doctor"
                        value={row.equipment || ''}
                        onChange={(e) => handleAmbulanceFareChange(row.id, 'equipment', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </td>
                    {/* Distance Slabs */}
                    {DISTANCE_SLABS.map((slab) => (
                      <td key={slab.key} className="py-3 px-2 text-center">
                        <input
                          type="text"
                          placeholder="—"
                          value={row.slabs?.[slab.key] || ''}
                          onChange={(e) => handleAmbulanceFareSlabChange(row.id, slab.key, e.target.value.replace(/\D/g, ''))}
                          className="w-20 py-2 px-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 text-center focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 font-medium"
                        />
                      </td>
                    ))}
                    {/* Outstation (₹/km) */}
                    <td className="py-3 px-3 text-center">
                      <input
                        type="text"
                        placeholder="₹/km"
                        value={row.outstation_rate || ''}
                        onChange={(e) => handleAmbulanceFareChange(row.id, 'outstation_rate', e.target.value.replace(/\D/g, ''))}
                        className="w-24 py-2 px-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 text-center font-semibold focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </td>
                    {/* Remove Action */}
                    <td className="py-3 px-2 text-center">
                      {currentCity.ambulanceFares.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveAmbulanceFareRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CARD 3: Add-ons by distance */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-slate-800">Add-ons by distance</h3>
              <p className="text-xs text-slate-400">Additional medical staff, equipment & specialty addons</p>
            </div>
            <button
              type="button"
              onClick={handleAddAddonRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-medium text-blue-600 hover:bg-blue-50/50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto pb-3">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 font-semibold whitespace-nowrap">
                  <th className="py-2.5 px-3 w-10 text-center">#</th>
                  <th className="py-2.5 px-3 min-w-[170px]">Add-on</th>
                  <th className="py-2.5 px-3 min-w-[200px]">Details</th>
                  {DISTANCE_SLABS.map((slab) => (
                    <th key={slab.key} className="py-2.5 px-2.5 min-w-[95px] text-center font-semibold text-slate-600">
                      {slab.label}
                    </th>
                  ))}
                  <th className="py-2.5 px-3 min-w-[180px] text-center font-semibold text-slate-600">Outstation Charge / Inclusion</th>
                  <th className="py-2.5 px-2 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentCity.addonsByDistance.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-semibold text-slate-500 text-center">{idx + 1}</td>
                    {/* Add-on selection */}
                    <td className="py-3 px-3">
                      <div className="relative">
                        <select
                          value={row.addon}
                          onChange={(e) => handleAddonChange(row.id, 'addon', e.target.value)}
                          className="w-full py-2 pl-3 pr-7 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select an option</option>
                          {ADDON_TYPES.map((a) => (
                            <option key={a} value={a}>{a}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    {/* Details input */}
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="Details"
                        value={row.details || ''}
                        onChange={(e) => handleAddonChange(row.id, 'details', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </td>
                    {/* Distance Slabs */}
                    {DISTANCE_SLABS.map((slab) => (
                      <td key={slab.key} className="py-3 px-2 text-center">
                        <input
                          type="text"
                          placeholder="—"
                          value={row.slabs?.[slab.key] || ''}
                          onChange={(e) => handleAddonSlabChange(row.id, slab.key, e.target.value.replace(/\D/g, ''))}
                          className="w-20 py-2 px-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 text-center focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 font-medium"
                        />
                      </td>
                    ))}
                    {/* Outstation Charge / Inclusion */}
                    <td className="py-3 px-3 text-center">
                      <input
                        type="text"
                        placeholder="Charge or Included"
                        value={row.outstation_charge || ''}
                        onChange={(e) => handleAddonChange(row.id, 'outstation_charge', e.target.value)}
                        className="w-36 py-2 px-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 text-center focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 font-medium"
                      />
                    </td>
                    {/* Remove Action */}
                    <td className="py-3 px-2 text-center">
                      {currentCity.addonsByDistance.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveAddonRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CARD 4: Other charges */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-slate-800">Other charges</h3>
              <p className="text-xs text-slate-400">Ancillary services and supplementary equipment rates</p>
            </div>
            <button
              type="button"
              onClick={handleAddOtherChargeRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-medium text-blue-600 hover:bg-blue-50/50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto pb-2">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 font-semibold">
                  <th className="py-2.5 px-3 w-10 text-center">#</th>
                  <th className="py-2.5 px-3 min-w-[200px]">Service / Item</th>
                  <th className="py-2.5 px-3 min-w-[140px]">Amount (₹)</th>
                  <th className="py-2.5 px-3 min-w-[180px]">Charged Per</th>
                  <th className="py-2.5 px-3 min-w-[220px]">Notes / Inclusions</th>
                  <th className="py-2.5 px-2 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentCity.otherCharges.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-semibold text-slate-500 text-center">{idx + 1}</td>
                    {/* Service / Item */}
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="e.g. Home Freezer Box"
                        value={row.service_item || ''}
                        onChange={(e) => handleOtherChargeChange(row.id, 'service_item', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </td>
                    {/* Amount */}
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="e.g. 2500"
                        value={row.amount || ''}
                        onChange={(e) => handleOtherChargeChange(row.id, 'amount', e.target.value.replace(/\D/g, ''))}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 font-semibold"
                      />
                    </td>
                    {/* Charged Per */}
                    <td className="py-3 px-3">
                      <div className="relative">
                        <select
                          value={row.charged_per}
                          onChange={(e) => handleOtherChargeChange(row.id, 'charged_per', e.target.value)}
                          className="w-full py-2 pl-3 pr-7 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select an option</option>
                          {CHARGED_PER_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    {/* Notes / Inclusions */}
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="Notes or conditions"
                        value={row.notes || ''}
                        onChange={(e) => handleOtherChargeChange(row.id, 'notes', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </td>
                    {/* Remove Action */}
                    <td className="py-3 px-2 text-center">
                      {currentCity.otherCharges.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveOtherChargeRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CARD 5: Waiting charges */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="pb-4 mb-4 border-b border-slate-100">
            <h3 className="text-sm sm:text-base font-semibold text-slate-800">
              Waiting charges
            </h3>
            <p className="text-xs text-slate-400">Detention and halte fare policy</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Free Waiting (Minutes)
              </label>
              <input
                type="text"
                placeholder="Enter free waiting (minutes)"
                value={currentCity.waitingCharges?.free_waiting_minutes || ''}
                onChange={(e) => handleWaitingChargesChange('free_waiting_minutes', e.target.value.replace(/\D/g, ''))}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                After Free Period (₹/hour)
              </label>
              <input
                type="text"
                placeholder="Enter after free period (₹/hour)"
                value={currentCity.waitingCharges?.after_free_period_rate || ''}
                onChange={(e) => handleWaitingChargesChange('after_free_period_rate', e.target.value.replace(/\D/g, ''))}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Waiting Terms
            </label>
            <textarea
              rows={3}
              placeholder="Enter details"
              value={currentCity.waitingCharges?.waiting_terms || ''}
              onChange={(e) => handleWaitingChargesChange('waiting_terms', e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all resize-y"
            />
          </div>
        </div>

        {/* CARD 6: Rate card notes */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="pb-4 mb-4 border-b border-slate-100">
            <h3 className="text-sm sm:text-base font-semibold text-slate-800">
              Rate card notes
            </h3>
            <p className="text-xs text-slate-400">Special contract terms, night rates & toll conditions</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Inclusions / Exclusions / Other Terms
            </label>
            <textarea
              rows={3}
              placeholder="Enter details"
              value={currentCity.rateCardNotes?.notes || ''}
              onChange={(e) => handleRateCardNotesChange(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all resize-y"
            />
          </div>
        </div>

        {/* Bottom Form Action Buttons with Blue Primary Action */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={goBack}
            className="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
            >
              <Bookmark className="w-4 h-4 text-slate-500" />
              <span>{isSaving ? 'Saving...' : 'Save & exit'}</span>
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transition-all cursor-pointer"
            >
              <span>Save & continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step4Rates;
